import { useState, useEffect, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import { supabase } from '@/lib/supabase';

interface BankAccount {
  id: string;
  bank_name: string;
  account_number: string;
  account_holder: string;
}

interface DepositHistory {
  id: string;
  amount: number;
  status: string;
  transaction_code: string | null;
  rejection_reason: string | null;
  created_at: string;
}

const statusStyle: Record<string, string> = {
  pending: 'bg-accent-500/15 text-accent-400',
  approved: 'bg-emerald-500/15 text-emerald-400',
  rejected: 'bg-rose-500/15 text-rose-400',
};

const statusLabel: Record<string, string> = {
  pending: 'Chờ xử lý',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
};

export default function Deposit() {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [amount, setAmount] = useState('');
  const [history, setHistory] = useState<DepositHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData.session?.user.id;
        if (!userId) return;

        const [{ data: banksRaw }, { data: depositsRaw }] = await Promise.all([
          supabase.rpc('get_user_banks_json', { p_user_id: userId }),
          supabase.rpc('get_user_deposit_requests_json', { p_user_id: userId }),
        ]);

        const banks = Array.isArray(banksRaw) ? banksRaw : JSON.parse((banksRaw as string) || '[]');
        const deposits = Array.isArray(depositsRaw) ? depositsRaw : JSON.parse((depositsRaw as string) || '[]');

        setBankAccounts(banks || []);

        setHistory(deposits.slice(0, 10) || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatAmount = (val: string) => {
    const num = val.replace(/[^0-9]/g, '');
    return num ? Number(num).toLocaleString('vi-VN') : '';
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const numAmount = Number(amount.replace(/[^0-9]/g, ''));
    if (!numAmount || numAmount < 50000) {
      setError('Số tiền tối thiểu là 50.000đ.');
      return;
    }

    setSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user.id;
      if (!userId) throw new Error('Bạn chưa đăng nhập.');

      const { error: insertError } = await supabase.from('deposit_requests').insert({
        user_id: userId,
        amount: numAmount,
        status: 'pending',
      });

      if (insertError) throw insertError;

      setSuccess('Yêu cầu nạp tiền đã được gửi. Vui lòng chờ admin duyệt.');
      setAmount('');

      const { data: newHistoryRaw } = await supabase.rpc('get_user_deposit_requests_json', { p_user_id: userId });
      const newHistory = Array.isArray(newHistoryRaw) ? newHistoryRaw : JSON.parse((newHistoryRaw as string) || '[]');
      setHistory(newHistory.slice(0, 10) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gửi yêu cầu thất bại.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500';
  const labelCls = 'block text-xs font-semibold text-foreground-600 mb-1.5';

  return (
    <AccountLayout>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-foreground-950 mb-4 md:mb-6">Nạp tiền vào ví</h1>

        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-2.5 md:p-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-rose-400 text-base"></i>
            <p className="text-sm text-rose-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-2.5 md:p-3 flex items-center gap-2">
            <i className="ri-check-line text-emerald-400 text-base"></i>
            <p className="text-sm text-emerald-400">{success}</p>
          </div>
        )}

        {bankAccounts.length === 0 ? (
          <div className="rounded-xl border border-accent-500/30 bg-accent-500/10 p-4 md:p-5 mb-6 flex items-start gap-3">
            <i className="ri-bank-line text-accent-400 text-xl mt-0.5"></i>
            <div>
              <p className="text-sm font-semibold text-foreground-950">Bạn chưa liên kết tài khoản ngân hàng</p>
              <p className="text-sm text-foreground-600 mt-1">Vui lòng liên kết ngân hàng trước khi nạp tiền.</p>
              <Link to="/ngan-hang" className="inline-block mt-2 text-sm font-semibold text-accent-400 hover:text-accent-300 cursor-pointer whitespace-nowrap">
                Liên kết ngân hàng ngay →
              </Link>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="rounded-xl border border-background-200/60 bg-background-100 p-4 md:p-5 mb-6 space-y-4">
              <h3 className="text-sm font-semibold text-foreground-950">Thông tin nạp tiền</h3>

              <div>
                <label className={labelCls}>Số tiền nạp (VNĐ)</label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(formatAmount(e.target.value))}
                  placeholder="VD: 1.000.000"
                  className={inputCls}
                />
                <p className="mt-1 text-xs text-foreground-500">Tối thiểu 50.000đ</p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors"
              >
                {submitting ? 'Đang gửi...' : 'Gửi yêu cầu nạp tiền'}
              </button>
            </form>
          </>
        )}

        <div className="rounded-xl border border-background-200/60 bg-background-100 p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-950 mb-4">Lịch sử nạp tiền</h3>
          {loading ? (
            <p className="text-sm text-foreground-600">Đang tải...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-foreground-600">Chưa có yêu cầu nạp tiền nào</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[500px]">
                <thead>
                  <tr className="text-left">
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-foreground-600">Số tiền</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-foreground-600">Trạng thái</th>
                    <th className="px-3 py-2 text-xs font-semibold uppercase text-foreground-600">Thời gian</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-background-200/40">
                      <td className="px-3 py-2 font-semibold text-foreground-950 whitespace-nowrap">
                        {Number(h.amount).toLocaleString('vi-VN')}đ
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${statusStyle[h.status]}`}>
                          {statusLabel[h.status]}
                        </span>
                        {h.status === 'rejected' && h.rejection_reason && (
                          <p className="text-xs text-rose-400 mt-1">{h.rejection_reason}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-foreground-600 whitespace-nowrap">
                        {new Date(h.created_at).toLocaleString('vi-VN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AccountLayout>
  );
}